import { listIdeonAgents } from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.client'
import type {
  AgentIndex,
  AgentsListMeta,
} from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getErrorMessage } from './getErrorMessage'

const DEFAULT_PER_PAGE = 20

export function useIdeonAgents() {
  const [agents, setAgents] = useState<AgentIndex[]>([])
  const [meta, setMeta] = useState<AgentsListMeta>({})
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  // Start in loading state so the first paint shows the table spinner instead
  // of a brief "No agents found" empty state before the initial fetch fires.
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [missingManagedAgentEnvVars, setMissingManagedAgentEnvVars] = useState<
    string[]
  >([])

  // Each fetch call bumps this version; only the latest call's results are
  // accepted, so rapid pagination changes can't be overwritten by an older
  // in-flight response landing late.
  const fetchVersionRef = useRef(0)

  const fetchAgents = useCallback(
    async (nextPage: number, nextPerPage: number) => {
      const version = ++fetchVersionRef.current
      setLoading(true)
      setLoadError(null)
      try {
        const data = await listIdeonAgents({
          page: nextPage,
          perPage: nextPerPage,
        })
        if (version !== fetchVersionRef.current) return
        setAgents(data.agents ?? [])
        setMeta(data.meta ?? {})
        setMissingManagedAgentEnvVars(data.missingManagedAgentEnvVars ?? [])
      } catch (err) {
        if (version !== fetchVersionRef.current) return
        console.error(err)
        const message = getErrorMessage(err)
        setLoadError(message)
        toast.error(`Failed to load agents: ${message}`)
        setAgents([])
        setMeta({})
        setMissingManagedAgentEnvVars([])
      } finally {
        if (version === fetchVersionRef.current) {
          setLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    void fetchAgents(page, perPage)
  }, [fetchAgents, page, perPage])

  const refresh = useCallback(
    () => fetchAgents(page, perPage),
    [fetchAgents, page, perPage]
  )

  return {
    agents,
    meta,
    page,
    perPage,
    loading,
    loadError,
    missingManagedAgentEnvVars,
    setPage,
    setPerPage,
    refresh,
  }
}
